import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

export type UserStatus = 'pending' | 'active' | 'inactive';

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  isSuperAdmin: boolean;
  status: UserStatus;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  groups: Array<{ id: string; code: string; name: string }>;
}

export interface AuditLog {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  userId: string | null;
  userEmail: string | null;
  changes: string | null;
  createdAt: string;
}

export interface SystemStats {
  users: number;
  customers: number;
  opportunities: number;
  projects: number;
  tarPlans: number;
  workstreams: number;
  capabilities: number;
}

// List users
export function useUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<{ users: User[]; total: number }>('/admin/users'),
  });
}

// Update user
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      status?: UserStatus;
      isSuperAdmin?: boolean;
      groupIds?: string[];
    }) => api.patch<{ user: User }>(`/admin/users/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

// Approve a pending user and assign them to groups
export function useApproveUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, groupIds }: { id: string; groupIds: string[] }) =>
      api.post<{ user: User }>(`/admin/users/${id}/approve`, { groupIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
    },
  });
}

// Delete a deactivated user (hard delete)
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ success: boolean }>(`/admin/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

// Get audit logs
export function useAuditLogs(params?: {
  entityType?: string;
  entityId?: string;
  userId?: string;
  userEmail?: string;
  action?: 'create' | 'update' | 'delete' | 'archive' | 'restore';
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['admin', 'audit-logs', params],
    queryFn: () => {
      const queryParams: Record<string, string | number | boolean | undefined> = {};
      if (params?.entityType) queryParams.entityType = params.entityType;
      if (params?.entityId) queryParams.entityId = params.entityId;
      if (params?.userId) queryParams.userId = params.userId;
      if (params?.userEmail) queryParams.userEmail = params.userEmail;
      if (params?.action) queryParams.action = params.action;
      if (params?.startDate) queryParams.startDate = params.startDate;
      if (params?.endDate) queryParams.endDate = params.endDate;
      if (params?.limit) queryParams.limit = params.limit;
      if (params?.offset) queryParams.offset = params.offset;

      return api.get<{
        logs: AuditLog[];
        total: number;
        limit: number;
        offset: number;
      }>('/admin/audit-logs', queryParams);
    },
  });
}

// Export audit logs URL generator
export function getAuditLogsExportUrl(params?: {
  entityType?: string;
  action?: string;
  userEmail?: string;
  startDate?: string;
  endDate?: string;
}): string {
  const searchParams = new URLSearchParams();
  if (params?.entityType) searchParams.set('entityType', params.entityType);
  if (params?.action) searchParams.set('action', params.action);
  if (params?.userEmail) searchParams.set('userEmail', params.userEmail);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  const query = searchParams.toString();
  return `/api/admin/audit-logs/export${query ? `?${query}` : ''}`;
}

// Get system stats
export function useSystemStats() {
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => api.get<SystemStats>('/admin/stats'),
  });
}
